import React from "react";

const MAP_STYLES = [
  {
    stylers: [{ visibility: "simplified" }, { saturation: 20 }, { weight: 3.2 }, { lightness: 25 }],
  },
  {
    featureType: "water",
    stylers: [{ weight: 0.1 }],
  },
  {
    featureType: "administrative.province",
    stylers: [{ visibility: "on" }, { weight: 1.5 }],
  },
  {
    featureType: "administrative.country",
    stylers: [{ visibility: "on" }, { weight: 1.5 }],
  },
];

const DEFAULT_ZOOM = 8;

const MapContext = React.createContext({
  mapInstance: null,
  mapContainerRef: null,
  isReady: false,
  currentPage: null,
  setCurrentPage: () => {},
  requestMap: () => {},
  searchResults: [],
  setSearchResults: () => {},
  getSearchResults: () => [],
  setCenter: () => {},
  fitBounds: () => {},
  getZoom: () => DEFAULT_ZOOM,
  getCenter: () => null,
  setDragEnabled: () => {},
  onDragEnd: () => {},
  mapZoom: null,
  setMapZoom: () => {},
  clearMapZoom: () => {},
  fittedResultsKey: null,
  setFittedResultsKey: () => {},
});
MapContext.displayName = "MapContext";

function MapProvider({ children }) {
  const mapRef = React.useRef(null);
  const mapContainerRef = React.useRef(null);
  const [isReady, setIsReady] = React.useState(false);
  const [mapsApiReady, setMapsApiReady] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(null);
  const [mapRequested, setMapRequested] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState([]);
  const [dragEnabled, setDragEnabled] = React.useState(false);
  const dragCallbackRef = React.useRef(null);
  const [mapZoom, setMapZoomState] = React.useState(null);
  const [fittedResultsKey, setFittedResultsKey] = React.useState(null);

  React.useEffect(() => {
    if (typeof google !== "undefined" && typeof google.maps !== "undefined") {
      setMapsApiReady(true);
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;

    function waitForMapsApi() {
      if (cancelled) return;

      if (typeof google !== "undefined" && typeof google.maps !== "undefined") {
        setMapsApiReady(true);
        return;
      }

      timeoutId = window.setTimeout(waitForMapsApi, 50);
    }

    waitForMapsApi();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  // Initialize once after the user explicitly opens the map view.
  React.useEffect(() => {
    if (!mapRequested) return;
    if (!mapContainerRef.current) return;
    if (!mapsApiReady) return;
    if (mapRef.current) return;

    const viewportWidth = typeof window !== "undefined" ? Math.max(document.documentElement.clientWidth, 0) : 1024;

    // Default center (Connecticut coordinates, will be updated by MapContent)
    const defaultCenter = { lat: 41.6, lng: -72.7 };

    const map = new google.maps.Map(mapContainerRef.current, {
      center: defaultCenter,
      zoom: DEFAULT_ZOOM,
      gestureHandling: "cooperative",
      styles: MAP_STYLES,
      mapTypeControl: viewportWidth >= 450,
      mapTypeControlOptions: viewportWidth >= 450
        ? { style: google.maps.MapTypeControlStyle.DROPDOWN_MENU }
        : undefined,
    });

    mapRef.current = map;
    setIsReady(true);
  }, [mapRequested, mapsApiReady]);

  React.useEffect(() => {
    return () => {
      if (mapRef.current && typeof google !== "undefined") {
        google.maps.event.clearInstanceListeners(mapRef.current);
        mapRef.current = null;
      }
    };
  }, []);

  // Handle dragend events
  React.useEffect(() => {
    if (!mapRef.current) return;

    const listener = google.maps.event.addListener(mapRef.current, "dragend", () => {
      // Only trigger callback if drag-to-search is enabled AND callback exists
      if (dragEnabled && dragCallbackRef.current) {
        const center = mapRef.current.getCenter();
        dragCallbackRef.current({
          lat: center.lat(),
          lng: center.lng(),
        });
      }
      // If dragEnabled is false, map still pans but no callback fires
    });

    return () => {
      if (listener) {
        google.maps.event.removeListener(listener);
      }
    };
  }, [dragEnabled]);

  const setCenter = React.useCallback((coords, zoom) => {
    if (!mapRef.current) return;
    mapRef.current.setCenter(coords);
    if (zoom !== undefined && zoom !== null) {
      mapRef.current.setZoom(zoom);
    }
  }, []);

  const fitBounds = React.useCallback((bounds) => {
    if (!mapRef.current) return;
    mapRef.current.fitBounds(bounds);
  }, []);

  const getZoom = React.useCallback(() => {
    return mapRef.current?.getZoom() || DEFAULT_ZOOM;
  }, []);

  const getCenter = React.useCallback(() => {
    if (!mapRef.current) return null;
    const center = mapRef.current.getCenter();
    return center ? { lat: center.lat(), lng: center.lng() } : null;
  }, []);

  const onDragEnd = React.useCallback((callback) => {
    dragCallbackRef.current = callback;
  }, []);

  const getSearchResults = React.useCallback(() => {
    return searchResults;
  }, [searchResults]);

  const setMapZoom = React.useCallback((zoom) => {
    setMapZoomState(zoom);
  }, []);

  const clearMapZoom = React.useCallback(() => {
    setMapZoomState(null);
  }, []);

  const requestMap = React.useCallback(() => {
    setMapRequested(true);
  }, []);

  const value = React.useMemo(
    () => ({
      mapInstance: mapRef.current,
      mapContainerRef,
      isReady,
      currentPage,
      setCurrentPage,
      requestMap,
      searchResults,
      setSearchResults,
      getSearchResults,
      setCenter,
      fitBounds,
      getZoom,
      getCenter,
      setDragEnabled,
      onDragEnd,
      mapZoom,
      setMapZoom,
      clearMapZoom,
      fittedResultsKey,
      setFittedResultsKey,
    }),
    [
      isReady,
      currentPage,
      searchResults,
      setCenter,
      fitBounds,
      getZoom,
      getCenter,
      onDragEnd,
      requestMap,
      getSearchResults,
      mapZoom,
      setMapZoom,
      clearMapZoom,
      fittedResultsKey,
    ]
  );

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

function useMap() {
  return React.useContext(MapContext);
}

export { MapContext, MapProvider, useMap, MAP_STYLES, DEFAULT_ZOOM };
